/*
 * Jira Ticket:
 * Created Date: Tue, 17th Mar 2020, 14:12:17 pm
 * Author: Jessica Mowatt
 * Email: jessica.mowatt@thedistance.co.uk
 * Copyright (c) 2020 The Distance
 */

import React, { useRef, useState } from 'react';
import {
  Edit,
  FormDataConsumer,
  NumberInput,
  ReferenceInput,
  required,
  SelectInput,
  SimpleForm,
  TextInput,
  useNotify,
  useTranslate,
  usePermissions,
  Title,
} from 'react-admin';
import debounce from 'lodash.debounce';
import { AccessDenied } from 'Layout';
import { useForm } from 'react-final-form';
import { SaveDeleteToolbar } from 'Components';
import { TextButton as SearchButton } from 'Components/Buttons';
import MapComponent from 'Components/Map';
import { columnStyles } from 'styles';
import { isProjectManager } from 'utils/roles';

const SanitizedForm = ({ canEdit, record }) => {
  const mapRef = useRef();
  const translate = useTranslate();
  const notify = useNotify();
  const classes = columnStyles();
  const [mapLoaded, setMapLoaded] = useState(false);
  const form = useForm();

  const delayedOnSearchClick = useRef(
    debounce(value => onSearchClick(value), 1000)
  ).current;

  const onSearchClick = value => {
    if (value && value.trim()) {
      const geocodingParams = {
        address: value,
      };
      return mapRef.current
        .performSearch(geocodingParams)
        .then(searchResult => form.change('location', searchResult))
        .catch(e => {
          if (process.env.NODE_ENV === 'development') console.error(e);
          if (e.messageArgs && e.messageArgs.hasOwnProperty('address')) {
            return notify(e.message, 'warning', {
              ...e.messageArgs,
            });
          }
          return notify(e.message, 'warning', {
            status: e.messageArgs,
          });
        });
    }
    return notify('error.maps.emptySearch', 'warning');
  };

  const onMapClick = coordinates => form.change('location', coordinates);

  return (
    <div className={classes.root}>
      <div>
        <div className={classes.autoHeightColumn}>
          <TextInput
            source="name"
            validate={required()}
            label={'resources.Site.fields.siteName'}
            disabled={!canEdit}
          />
          {/* use FormDataConsumer to get the orgId from project and feed back in to the drop down as state is cleared on form submission */}
          <FormDataConsumer>
            {({ formData }) => (
              <ReferenceInput
                label={'resources.Site.fields.project'}
                source="projectId"
                reference="Project"
                filter={{ organisationId: formData.project.organisation.id }}
                disabled={!canEdit}
                perPage={1000}
                validate={required()}
                sort={{ field: 'name', order: 'ASC' }}
              >
                <SelectInput
                  source="id"
                  optionText="name"
                  validate={required()}
                />
              </ReferenceInput>
            )}
          </FormDataConsumer>
        </div>
        {canEdit && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <div className={classes.autoHeightColumn}>
              <TextInput
                source="postCode"
                label={'resources.Site.fields.postCode'}
                validate={required()}
              />
            </div>
            <div>
              <FormDataConsumer>
                {({ formData }) => (
                  <SearchButton
                    label={translate('actions.search')}
                    onClick={() => delayedOnSearchClick(formData.postCode)}
                    style={{ width: '10vw' }}
                  />
                )}
              </FormDataConsumer>
            </div>
          </div>
        )}
        <div className={classes.autoHeightColumn}>
          <NumberInput
            source="location.lat"
            validate={required()}
            label={'resources.Site.fields.latitude'}
            disabled={mapLoaded ? mapLoaded : !canEdit} // if map loaded disable field, if not loaded check user permissions
          />
          <NumberInput
            source="location.lng"
            validate={required()}
            label={'resources.Site.fields.longitude'}
            disabled={mapLoaded ? mapLoaded : !canEdit} // if map loaded disable field, if not loaded check user permissions
          />
        </div>
      </div>
      <div className={classes.lastSiteColumn}>
        <NumberInput
          source="statistics.numberOfTowers"
          label={'resources.Site.fields.numberOfTowers'}
          className={classes.lastSiteColumnFields}
          disabled={true}
        />
        <NumberInput
          source="statistics.numberOfBuilders"
          className={classes.lastSiteColumnFields}
          label={'resources.Site.fields.numberOfBuilders'}
          disabled={true}
        />
        <NumberInput
          source="statistics.numberOfApprovers"
          className={classes.lastSiteColumnFields}
          label={'resources.Site.fields.numberOfApprovers'}
          disabled={true}
        />
        {/* only load the map when we have a location */}
        {record && record.location && (
          <MapComponent
            ref={mapRef}
            mapZoom={17}
            initialLocation={record && record.location}
            updateFormValues={coords => onMapClick(coords)}
            setMapLoaded={setMapLoaded}
            disableDefaultUI={!canEdit}
          />
        )}
      </div>
    </div>
  );
};

const SiteEdit = props => {
  const { permissions } = usePermissions();
  const { organisationId } = props.location.state || {};

  const canEdit = organisationId
    ? isProjectManager(permissions, organisationId)
    : false;

  const redirect = `/Site`;

  return isProjectManager(permissions) ? (
    <>
      <Title title={`resources.General.cmsTitle`} />
      <Edit {...props}>
        <SimpleForm
          redirect={redirect}
          submitOnEnter={false}
          toolbar={
            canEdit ? <SaveDeleteToolbar deleteRedirect={redirect} /> : null
          }
        >
          <SanitizedForm canEdit={canEdit} {...props} />
        </SimpleForm>
      </Edit>
    </>
  ) : (
    <AccessDenied />
  );
};

export default SiteEdit;
